import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';

import { requestUserRegister } from '@atom/actions/userActions';
import { useNavigation } from '@atom/hooks/useNavigation';
import { useTenantConnections } from '@atom/hooks/useTenantConnections';
import { Icon, IconButton, Progress, TextField } from '@atom/mui';
import { RemoveRedEyeIcon, VisibilityOffIcon } from '@atom/mui/icons';
import { RootState } from '@atom/types/store';
import accountUtilities from '@atom/utilities/accountUtilities';

import AuthTopBar from './AuthTopBar';
import LoginFooter from './LoginFooter';
import SsoButtonGroup from './SsoButtonGroup';

import classes from './login.module.css';

const styles = {
  passwordField: {
    paddingTop: '0.4rem',
  },
};

type RegistrationInput = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
};

type ErrorsState = {
  email: boolean;
  password: boolean;
  confirmPassword: boolean;
  firstName: boolean;
  lastName: boolean;
};

const Registration = () => {
  const [, { navigate }] = useNavigation();
  const dispatch = useDispatch();
  const loading = useSelector(
    (state: RootState) => state.loading.loadingUserRegistration,
  );
  const { connections, isLoading: isTenantLoading } = useTenantConnections();

  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const [input, setInput] = useState<RegistrationInput>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
  });
  const [errors, setErrors] = useState<ErrorsState>({
    email: false,
    confirmPassword: false,
    password: false,
    firstName: false,
    lastName: false,
  });

  const navigateToLogin = () => {
    navigate('/login');
  };

  const onChange = (property: keyof RegistrationInput, value: string) => {
    setInput(currentInput => ({
      ...currentInput,
      [property]: value,
    }));

    setErrors(currentErrors => ({
      ...currentErrors,
      [property]: false,
    }));
  };

  const validateValues = () => {
    const { email, firstName, lastName, password, confirmPassword } = input;

    const validEmail = accountUtilities.isValidEmail(email);
    const validFirstName = firstName;
    const validLastName = lastName;
    const validPassword = accountUtilities.isValidPassword(password);
    const validConfirmPassword =
      confirmPassword && password === confirmPassword;

    setErrors(currentErrors => ({
      ...currentErrors,
      email: !validEmail,
      firstName: !validFirstName,
      lastName: !validLastName,
      password: !validPassword,
      confirmPassword: !validConfirmPassword,
    }));

    return (
      validEmail &&
      validFirstName &&
      validLastName &&
      validPassword &&
      validConfirmPassword
    );
  };

  const submit = () => {
    const allValid = validateValues();

    if (allValid) {
      dispatch(
        requestUserRegister({
          firstName: input.firstName,
          lastName: input.lastName,
          email: input.email,
          password: input.password,
          phone: input.phone,
        }),
      );
    }
  };

  const content = isTenantLoading ? (
    <div className={classes.spinnerContainer}>
      <Progress />
    </div>
  ) : (
    <>
      <div className={clsx(classes.authBackground, classes.base)} />
      <div className={classes.headingText}>Let's get started</div>
      <div className={classes.textFieldContainer}>
        <div className={classes.nameRow}>
          <div className={classes.textFieldRegistration}>
            <TextField
              variant="standard"
              value={input.firstName}
              label="First Name"
              disabled={loading}
              onChange={event => onChange('firstName', event.target.value)}
              helperText={errors.firstName ? 'Please enter a first name.' : ''}
              error={errors.firstName}
            />
          </div>
          <div className={classes.textFieldRegistration}>
            <TextField
              variant="standard"
              value={input.lastName}
              label="Last Name"
              disabled={loading}
              onChange={event => onChange('lastName', event.target.value)}
              helperText={errors.lastName ? 'Please enter a last name.' : ''}
              error={errors.lastName}
            />
          </div>
        </div>
        <div className={classes.textFieldRegistration}>
          <TextField
            variant="standard"
            type="email"
            value={input.email}
            label="Email"
            disabled={loading}
            onChange={event => onChange('email', event.target.value)}
            helperText={
              errors.email ? 'Please enter a valid email address.' : ''
            }
            error={errors.email}
          />
        </div>
        <div className={clsx(classes.textFieldRegistration, 'fs-mask')}>
          <TextField
            variant="standard"
            style={styles.passwordField}
            type={passwordVisible ? 'text' : 'password'}
            value={input.password}
            label="Password"
            disabled={loading}
            onChange={event => onChange('password', event.target.value)}
            error={errors.password}
            slotProps={{
              input: {
                endAdornment: (
                  <IconButton
                    onClick={() =>
                      setPasswordVisible(currentValue => !currentValue)
                    }
                    size="small"
                  >
                    <Icon
                      icon={
                        passwordVisible ? VisibilityOffIcon : RemoveRedEyeIcon
                      }
                    />
                  </IconButton>
                ),
              },
            }}
          />
        </div>
        {errors.password && (
          <div className={classes.passwordError}>
            Please enter a password that is at least (12) characters long and
            includes at least one lowercase letter, uppercase letter, number,
            and special character (!@#$%^&*?) or a space.
          </div>
        )}
        <div className={clsx(classes.textFieldRegistration, 'fs-mask')}>
          <TextField
            variant="standard"
            style={styles.passwordField}
            type={passwordVisible ? 'text' : 'password'}
            value={input.confirmPassword}
            label="Confirm Password"
            disabled={loading}
            onChange={event => onChange('confirmPassword', event.target.value)}
            helperText={
              errors.confirmPassword
                ? 'Passwords do not match. Please confirm your password.'
                : ''
            }
            error={errors.confirmPassword}
            slotProps={{
              input: {
                endAdornment: (
                  <IconButton
                    onClick={() =>
                      setPasswordVisible(currentValue => !currentValue)
                    }
                    size="small"
                  >
                    <Icon
                      icon={
                        passwordVisible ? VisibilityOffIcon : RemoveRedEyeIcon
                      }
                    />
                  </IconButton>
                ),
              },
            }}
          />
        </div>
        <div className={classes.textFieldRegistration}>
          <TextField
            variant="standard"
            value={input.phone}
            label="Phone Number (Optional)"
            disabled={loading}
            onChange={event => onChange('phone', event.target.value)}
          />
        </div>
      </div>
      <div className={classes.genericButtonContainer}>
        <button
          className={classes.genericButton}
          onClick={submit}
          disabled={loading}
        >
          sign up
        </button>
      </div>
      <SsoButtonGroup verb="Sign up" connections={connections} />
    </>
  );

  return (
    <>
      <AuthTopBar
        prompt="Already have an account?"
        ctaLabel="Sign In"
        onCtaClick={navigateToLogin}
      />
      <div className={classes.container}>
        {content}
        <LoginFooter />
      </div>
    </>
  );
};

export default Registration;
